'use client';

import { Item } from '@/api';
import Container from '@/components/Layout/Container';
import ProductTile from '@/components/ecommerce/ProductTile/ProductTile';
import Pagination from '@/components/general/Pagination';
import { SearchResponse, searchQueryFn } from '@/queries/search';
import { DisplaySortOptions, DisplaySortOptionsType, SortOptions } from '@/types/API';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import i18next from 'i18n';
import Link from 'next/link';
import EmptyState from '@/components/general/EmptyState';
import { Constants } from '@/helpers/Constants';
import xor from 'lodash.xor';
import isNil from 'lodash.isnil';
import { useDraftMode } from '@/hooks/draft-mode';
import SearchFilters, { DefaultFilters } from '../ecommerce/search/SearchFilters';

export interface SearchProps {
    anchor?: string;
    defaultFilter?: DefaultFilters
    defaultSort?: SortOptions
    defaultLimit?: number
    isDefaultFilterFixed?: boolean
    searchTitle?: string
    hideTitle?: boolean
    hideFilter?: boolean
    /**
     * Show the result count beside the title. Only the search pages do: on a
     * category or sub-category the heading is the category's name, and a bare
     * number beside it reads as part of that name rather than as a tally of
     * what a query turned up.
     */
    showCount?: boolean
    searchQuery?: string
    storeSearchInUrl?: boolean;
    onDefaultFilterChanged?: () => void
    defer?: boolean
    /**
     * Force-include QA-active items (is_qa) regardless of draft mode. Used by
     * the page-builder Filter block so curated grids show items that aren't
     * `searchable` yet. The global search page leaves this off, so global
     * search stays filtered to searchable items.
     */
    showQaItems?: boolean
}

const arrayEqual = (arr1?: unknown[], arr2?: unknown[]) => arr1 && arr2 && arr1.length === arr2.length && xor(arr1, arr2).length === 0;

export function Search(props: SearchProps) {
    const {
        defer, anchor, searchTitle, searchQuery, defaultFilter, isDefaultFilterFixed = false,
        onDefaultFilterChanged, defaultSort, hideTitle = false, hideFilter = false, defaultLimit,
        storeSearchInUrl = true, showQaItems = false, showCount = false
    } = props;
    const draftMode = useDraftMode();
    const searchParams = useSearchParams();
    const searchTopRef = useRef<HTMLDivElement>(null);
    // The grid is four-up at every desktop width now, so page size is a fixed
    // multiple of four. Previously this switched to 5/25 above 1440px, which
    // leaves a ragged final row against a four-column grid.
    const PAGE_FACTOR = 4;
    const DEFAULT = 24;
    const [itemsPerPage, setItemsPerPage] = useState(defaultLimit || DEFAULT);

    const [offset, setOffset] = useState<number>(0);

    const [data, setData] = useState<SearchResponse | null>(null);

    const [initDone, setInitDone] = useState<boolean>(false);

    const [artistsFilters, setArtistsFilters] = useState<string[]>([]);
    const [offerTypesFilters, setOfferTypesFilters] = useState<string[]>([]);
    const [conditionsFilters, setConditionsFilters] = useState<string[]>([]);
    const [partnersFilters, setPartnersFilters] = useState<string[]>([]);
    const [genresFilters, setGenresFilters] = useState<string[]>([]);
    const [categoriesFilters, setCategoriesFilters] = useState<string[]>([]);
    const [subCategoriesFilters, setSubCategoriesFilters] = useState<string[]>([]);
    const [collectionsFilters, setCollectionsFilter] = useState<string[]>([]);

    const defaultSortMem = useMemo(() => {
        return (defaultSort ? DisplaySortOptions.find((o) => o.value === defaultSort) : undefined) || DisplaySortOptions[0];
    }, [defaultSort]);

    const [sortBy, setSortBy] = useState<DisplaySortOptionsType | null>(defaultSortMem);

    const searchMutation = useMutation({
        mutationFn: searchQueryFn,
        onError: async (error) => {
        }
    });

    /*
     * Typing in the header drives `searchQuery` now, so several searches can
     * be in flight at once and they do not necessarily come back in order:
     * "elt", issued first, can resolve after "elton" and overwrite the newer
     * results with older ones under a URL that says otherwise. Results are
     * applied per request instead of from a shared onSuccess, and only the
     * most recent request is allowed to land.
     */
    const latestRequest = useRef(0);

    const isMountingRef = useRef(false);
    const prevOffset = useRef(offset);

    useEffect(() => {
        if (isMountingRef.current === false) {
            isMountingRef.current = true;
        } else if (prevOffset.current !== offset) {
            prevOffset.current = offset;
            searchTopRef.current?.scrollIntoView({
                block: 'start'
            });
        }
    }, [offset]);

    useEffect(() => {
        const { subcategories, categories, artists, genres, conditions, partners, collections, offerTypes } = (defaultFilter || {});
        setArtistsFilters(searchParams?.get('artists')?.split(',') || artists || []);
        setOfferTypesFilters(searchParams?.get('offerTypes')?.split(',') || offerTypes || []);
        setConditionsFilters(searchParams?.get('conditions')?.split(',') || conditions || []);
        setPartnersFilters(searchParams?.get('partners')?.split(',') || partners || []);
        setCategoriesFilters(searchParams?.get('categories')?.split(',') || categories || []);
        setSubCategoriesFilters(searchParams?.get('sub_categories')?.split(',') || subcategories || []);
        setGenresFilters(searchParams?.get('genres')?.split(',') || genres || []);
        setCollectionsFilter(searchParams?.get('collections')?.split(',') || collections || []);

        if (searchParams?.get('sort_by') && Object.values(SortOptions).includes(searchParams?.get('sort_by') as SortOptions)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            setSortBy(DisplaySortOptions.find((o) => o.value === (searchParams?.get('sort_by') as SortOptions))!);
        }

        let shouldSetOffset = false;
        try {
            const userSettingPageSize = localStorage.getItem('page_size');
            if (userSettingPageSize && isNil(defaultLimit)) {
                setItemsPerPage(parseInt(userSettingPageSize as string, 10));
                shouldSetOffset = true;
            }
            // eslint-disable-next-line no-empty
        } catch (error) {
        }
        if (searchParams?.get('offset') && !Number.isNaN(parseInt(searchParams?.get('offset') as string, 10))) {
            setOffset(parseInt(searchParams?.get('offset') as string, 10));
            shouldSetOffset = false;
        }
        if (shouldSetOffset) {
            setOffset(0);
        }
        setInitDone(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!initDone) {
            return;
        }

        const requestId = latestRequest.current + 1;
        latestRequest.current = requestId;

        searchMutation.mutate({
            query: searchQuery || '',
            limit: itemsPerPage,
            offset,
            artists: artistsFilters,
            offerTypes: offerTypesFilters,
            genres: genresFilters,
            conditions: conditionsFilters,
            partners: partnersFilters,
            categories: categoriesFilters,
            subCategories: subCategoriesFilters,
            collections: collectionsFilters,
            sortBy: sortBy?.value,
            isPreview: draftMode || showQaItems
        }, {
            onSuccess: (response) => {
                if (requestId === latestRequest.current) {
                    setData(response);
                }
            }
        });

        if (itemsPerPage !== DEFAULT && itemsPerPage !== defaultLimit) {
            try {
                localStorage.setItem('page_size', `${itemsPerPage}`);
                // eslint-disable-next-line no-empty
            } catch (error) {
            }
        } else {
            localStorage.removeItem('page_size');
        }

        if (!storeSearchInUrl) {
            return;
        }

        const current = new URLSearchParams(Array.from(searchParams?.entries()));

        const genresValue = genresFilters.join(',');
        if (!genresValue || (arrayEqual(genresFilters, defaultFilter?.genres))) {
            current.delete('genres');
        } else {
            current.set('genres', genresValue);
        }

        const artistValue = artistsFilters.join(',');
        if (!artistValue || (arrayEqual(artistsFilters, defaultFilter?.artists))) {
            current.delete('artists');
        } else {
            current.set('artists', artistValue);
        }

        const offerTypesValue = offerTypesFilters.join(',');
        if (!offerTypesValue || (arrayEqual(offerTypesFilters, defaultFilter?.offerTypes))) {
            current.delete('offerTypes');
        } else {
            current.set('offerTypes', offerTypesValue);
        }

        const conditionsValue = conditionsFilters.join(',');
        if (!conditionsValue || (arrayEqual(conditionsFilters, defaultFilter?.conditions))) {
            current.delete('conditions');
        } else {
            current.set('conditions', conditionsValue);
        }

        const partnersValue = partnersFilters.join(',');
        if (!partnersValue || (arrayEqual(partnersFilters, defaultFilter?.partners))) {
            current.delete('partners');
        } else {
            current.set('partners', partnersValue);
        }

        const categoriesValue = categoriesFilters.join(',');
        if (!categoriesValue || (arrayEqual(categoriesFilters, defaultFilter?.categories))) {
            current.delete('categories');
        } else {
            current.set('categories', categoriesValue);
        }

        const subCategoriesValue = subCategoriesFilters.join(',');
        if (!subCategoriesValue || (arrayEqual(subCategoriesFilters, defaultFilter?.subcategories))) {
            current.delete('sub_categories');
        } else {
            current.set('sub_categories', subCategoriesValue);
        }

        const collectionsValue = collectionsFilters.join(',');
        if (!collectionsValue || (arrayEqual(collectionsFilters, defaultFilter?.collections))) {
            current.delete('collections');
        } else {
            current.set('collections', collectionsValue);
        }

        if (!sortBy?.value || sortBy.value === defaultSortMem.value) {
            current.delete('sort_by');
        } else {
            current.set('sort_by', sortBy.value);
        }

        if (offset > 0) {
            current.set('offset', `${offset}`);
        } else {
            current.delete('offset');
        }

        const search = current.toString();
        // window.location, not usePathname: this effect also runs when the
        // term changes, and a stale React pathname here would rewrite the URL
        // back to the previous term and fight the router.
        window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, categoriesFilters, categoriesFilters, artistsFilters, genresFilters, collectionsFilters, conditionsFilters, partnersFilters, subCategoriesFilters, offset, sortBy, itemsPerPage, offerTypesFilters, defaultSortMem]);

    /*
     * `fullWidth` let the results run edge to edge. The comp
     * (TR - Page Designs [MASTER] 261:10433) is a 1360 frame with 48px
     * padding, so the content column is 1264 — exactly `max-w-outer`, which
     * is what `flush` caps to while keeping a 16px gutter below the cap. The
     * inner md:px-[40px] and pr-[60px] were compensating for the full bleed
     * and would now eat 80px out of the column, so they went too.
     */
    return (
        <Container flush>
            <div id={anchor} ref={searchTopRef} className="pt-[24px]">
                {/*
                  * Title and controls share one row, as the comp draws them:
                  * term and count left, dropdowns and Sort right. Was a
                  * centred font-title h3 stacked above the toolbar.
                  *
                  * min-w-0 is what makes the h1's `truncate` bite — a flex item
                  * defaults to min-width:auto, so without it the title pushes
                  * the controls off instead of ellipsing. The count is
                  * shrink-0 so it never gets clipped in the title's place.
                  */}
                <div className="flex flex-col gap-[8px]">
                    {!hideTitle && (
                        <div className="flex min-w-0 items-center gap-[12px]">
                            <h1 className="text-h2 truncate text-ink-400">{searchTitle}</h1>
                            {/*
                              * A literal rather than text-ink-200, deliberately.
                              * The master's Text/200 is #595958, but our
                              * --ds-ink-200 is #A3A298 — the token is off, and
                              * its own note in globals.scss flags it as an open
                              * question (Q5). Correcting the token would recolour
                              * all 38 places that use it, which is a call to make
                              * on its own; this is the one that was asked for.
                              */}
                            {showCount && typeof data?.pagination?.total === 'number' && (
                                <span className="text-h2 shrink-0 text-[#595958]">{data.pagination.total}</span>
                            )}
                        </div>
                    )}
                    {!hideFilter &&
                        /*
                         * The controls own the row under the title rather than
                         * sharing one with it. Beside the title they were
                         * squeezed into whatever the term left them, which is
                         * what cut "Sub Categories" in half; across the full
                         * width they can share it out between themselves.
                         */
                        <div className="w-full">
                            <SearchFilters
                                defaultFilters={defaultFilter}
                                isDefaultFilterFixed={isDefaultFilterFixed}
                                meta_available={data?.meta_available}
                                artistsFilters={artistsFilters}
                                conditionsFilters={conditionsFilters}
                                partnersFilters={partnersFilters}
                                genresFilters={genresFilters}
                                collectionsFilters={collectionsFilters}
                                categoriesFilters={categoriesFilters}
                                subCategoriesFilters={subCategoriesFilters}
                                offerTypeFilters={offerTypesFilters}
                                sortBy={sortBy}
                                defaultSortBy={defaultSortMem}
                                setOfferTypeFilters={(filter) => {
                                    if (defaultFilter?.offerTypes && filter.length < 1 && onDefaultFilterChanged) {
                                        onDefaultFilterChanged();
                                    } else {
                                        setOffset(0);
                                        setOfferTypesFilters(filter);
                                    }
                                }}
                                setArtistsFilters={(filter) => {
                                    if (defaultFilter?.artists && filter.length < 1 && onDefaultFilterChanged) {
                                        onDefaultFilterChanged();
                                    } else {
                                        setOffset(0);
                                        setArtistsFilters(filter);
                                    }
                                }}
                                setCollectionsFilters={(filter) => {
                                    if (defaultFilter?.collections && filter.length < 1 && onDefaultFilterChanged) {
                                        onDefaultFilterChanged();
                                    } else {
                                        setOffset(0);
                                        setCollectionsFilter(filter);
                                    }
                                }}
                                setConditionsFilters={(filter) => {
                                    if (defaultFilter?.conditions && filter.length < 1 && onDefaultFilterChanged) {
                                        onDefaultFilterChanged();
                                    } else {
                                        setOffset(0);
                                        setConditionsFilters(filter);
                                    }
                                }}
                                setPartnersFilters={(filter) => {
                                    if (defaultFilter?.partners && filter.length < 1 && onDefaultFilterChanged) {
                                        onDefaultFilterChanged();
                                    } else {
                                        setOffset(0);
                                        setPartnersFilters(filter);
                                    }
                                }}
                                setCategoriesFilters={(filter) => {
                                    if (defaultFilter?.categories && filter.length < 1 && onDefaultFilterChanged) {
                                        onDefaultFilterChanged();
                                    } else {
                                        setOffset(0);
                                        setCategoriesFilters(filter);
                                    }
                                }}
                                setGenresFilters={(filter) => {
                                    if (defaultFilter?.genres && filter.length < 1 && onDefaultFilterChanged) {
                                        onDefaultFilterChanged();
                                    } else {
                                        setOffset(0);
                                        setGenresFilters(filter);
                                    }
                                }}
                                setSubCategoriesFilters={(filter) => {
                                    if (defaultFilter?.subcategories && filter.length < 1 && onDefaultFilterChanged) {
                                        onDefaultFilterChanged();
                                    } else {
                                        setOffset(0);
                                        setSubCategoriesFilters(filter);
                                    }
                                }}
                                setSortBy={setSortBy}
                                loading={data === null && searchMutation.isPending}
                            />
                        </div>
                    }
                </div>
                <div>
                    {/*
                      * Skeletons are for the first load only. Swapping them in
                      * on every `isPending` is what made search-as-you-type
                      * flash: each keystroke replaced the grid with grey
                      * blocks and then rebuilt it. The previous results stay
                      * up, dimmed, until the new ones arrive.
                      */}
                    {/*
                      * With results the holder is the grid; without them it is a
                      * flex box, so the empty state sits in the middle of the
                      * minimum height rather than at the top of it — it asks to
                      * fill its parent, which does nothing unless the parent is
                      * a flex container.
                      *
                      * 60vh on desktop, but never less than 620. The layout
                      * wraps every page in min-h-[700px], and an empty result
                      * set only came to 581 — so the wrapper kept the
                      * difference as dead space above the footer. This area
                      * plus the rest of the page is 81, so 620 is what clears
                      * 700 on a short screen, and 60vh takes over on a tall
                      * one. Four elements sit between this and that wrapper,
                      * two of them shared by every page, so growing the area
                      * from here beats making all of them flex.
                      */}
                    {data === null ?
                        <div className="">
                            <div className="mb-[24px] grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4">
                                {/*
                                  * One per column, shaped like the card rather
                                  * than a flat block: a square photo, then the
                                  * title's two lines and the price. It was five
                                  * 350px blocks — five because the grid used to
                                  * go 5-up above 1440, so the last one wrapped
                                  * onto a second row, and 350 against a card
                                  * that measures 464, so the grid jumped when
                                  * the results arrived.
                                  */}
                                {Array.from({ length: 4 }).map((_, i) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <div key={`tile-skeleton-${i}`} className="md:my-[13px] mb-[24px]">
                                        <Skeleton className="aspect-square w-full" />
                                        <div className="py-[16px]">
                                            <div className="mb-[20px]">
                                                <Skeleton height={24} count={2} />
                                                <Skeleton height={21} width="40%" />
                                            </div>
                                            <Skeleton height={24} width="55%" />
                                            <Skeleton height={21} width="35%" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div> :
                        <div className={clsx('min-h-[500px] md:min-h-[max(620px,60vh)] mb-[24px] transition-opacity duration-dropdown ease-ds-out', searchMutation.isPending && 'opacity-50', data && data.results && data?.results?.length > 0
                            ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4'
                            : 'flex')}>
                            {data && data.results && data?.results?.length > 0 ?
                                data.results.map((searchResult: Item, i) => (
                                    /*
                                     * No rule under a mobile card. The tile
                                     * master (45:4351) separates cards with
                                     * space, and this one was a legacy #ccc —
                                     * a cool grey with no place in the warm
                                     * ramp — drawn under every cell including
                                     * the last, where it had nothing to
                                     * divide. The 16px that gave it room goes
                                     * with it; the 40px between rows stays.
                                     */
                                    <div key={searchResult.id} className="mb-[40px] md:my-[13px] md:mb-[0px]">
                                        <ProductTile fullWidth defer={defer || i > 15} data={searchResult} />
                                    </div>
                                )) :
                                <EmptyState
                                    icon="search"
                                    title={i18next.t('search.no-results')}
                                    subtitle={(
                                        <>
                                            {i18next.t('search.no-results-body')}{' '}
                                            <Link
                                                prefetch={false}
                                                href={Constants.Support.HELP_CENTER}
                                                target="_blank"
                                                className="underline"
                                            >
                                                {i18next.t('search.contact-support')}
                                            </Link>
                                        </>
                                    )}
                                    actionText={i18next.t('search.browse-marketplace')}
                                    pathRoute="/marketplace"
                                />
                            }
                        </div>
                    }

                    <Pagination
                        factor={PAGE_FACTOR}
                        itemsPerPageChanged={setItemsPerPage}
                        defaultItemsPerPage={itemsPerPage}
                        data={data?.pagination}
                        offsetChanged={setOffset} />

                </div>
            </div>
        </Container>
    );
}
